import { useRef } from "react";
import { motion } from "framer-motion";
import { ChapterLabel, FadeUp } from "./Shared";
import { CONTACT } from "@/veedu/data";

const OurStory = () => {
  return (
    <section
      id="story"
      data-chapter
      data-testid="story-section"
      className="relative min-h-[80vh] flex items-center overflow-hidden bg-cream bg-cover bg-no-repeat bg-[position:right_35%_center] lg:bg-[position:right_center] py-10 md:py-14"
      style={{
        backgroundImage: "url('/Images/about_bg.jpeg')",
      }}
    >
      {/* Background Gradient Overlays for smooth transition & readability */}
      {/* Mobile: Solid parchment overlay */}
      <div className="absolute inset-0 bg-cream/95 backdrop-blur-[1px] md:hidden pointer-events-none" />

      {/* Desktop: Smooth gradient overlay giving spotlight to text while showing artwork */}
      <div
        className="absolute inset-0 pointer-events-none hidden md:block"
        style={{
          background:
            "linear-gradient(to right, rgba(244,241,234,0.98) 0%, rgba(244,241,234,0.95) 50%, rgba(244,241,234,0.82) 65%, rgba(244,241,234,0.3) 85%, transparent 100%)",
        }}
      />

      {/* Main Content Area — shifted left to sit nicely in the open parchment space */}
      <div className="relative z-10 w-full px-6 md:pl-[12%] lg:pl-[14%] xl:pl-[16%] pr-6 py-10 md:py-14">
        <div className="max-w-[100%] md:max-w-[420px] lg:max-w-[440px]">
          {/* Main Heading & Subtitle Quote */}
          <FadeUp delay={0.1}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-normal text-[#1A0F0A] tracking-tight leading-[1.1]">
              Our Story
              <span className="text-terra font-display italic font-semibold tracking-normal text-[0.55em] sm:text-[0.6em] block mt-1.5 leading-snug">
                “Come and listen to the food tales of Kerala”
                <span className="not-italic font-sans text-[0.75em] tracking-wider uppercase opacity-90 flex items-center gap-2 mt-2 font-medium">
                  <span>—</span>
                  <img 
                    src="/Images/Chef Jomon Text.png" 
                    alt="Chef Jomon" 
                    className="h-5 sm:h-6 w-auto object-contain -mt-1" 
                  />
                </span>
              </span>
            </h2>
          </FadeUp>

          {/* Decorative Divider */}
          <FadeUp delay={0.2} className="flex items-center gap-4 my-5 md:my-6">
            <span className="h-[1px] w-12 bg-dark-brown/30" />
            <span className="text-[10px] text-brass select-none">❖</span>
            <span className="h-[1px] w-12 bg-dark-brown/30" />
          </FadeUp>

          {/* Body Copy */}
          <FadeUp delay={0.3} className="space-y-4 md:space-y-4.5 text-[#1A0F0A] font-body text-sm sm:text-base leading-relaxed">
            <h3 className="font-display text-base sm:text-lg font-bold tracking-wider text-[#1A0F0A] uppercase">
              A LIFE SHAPED BY FLAVOUR
            </h3>

            <p className="font-semibold text-[#1A0F0A]">
              Come and listen to the food tales of Kerala.
            </p>

            <p className="font-medium text-[#1A0F0A]">
              I grew up in Kerala, where food was never simply something we ate. It was part of everyday life — the aroma of spices in the kitchen, fresh coconut, tamarind, curry leaves, seafood from the coast, and the warmth of a meal shared with family.
            </p>

            <p className="font-medium text-[#1A0F0A]">
              From my mother’s kitchen to the small local places I discovered growing up, I was always searching for flavour. I became fascinated by how different ingredients, regions and traditions could create completely different experiences.
            </p>

            <p className="font-medium text-[#1A0F0A]">
              That curiosity eventually led me into hospitality and became the foundation of my life as a chef.
            </p>

            <p className="font-medium text-[#1A0F0A]">
              Years later, I carried those memories with me to England. At 197 High Street, an old timber-framed building became the setting for something new — a place where the flavours, traditions and memories of Kerala could find a new home.
            </p>

            <p className="font-medium text-[#1A0F0A]">
              And so <span className="text-terra font-bold font-sans tracking-wide">VEEDU</span> was born — a meeting of Kerala and England, of heritage and hospitality. Here, the past is brought to the table, one recipe, one story and one shared meal at a time.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

export default OurStory;
